import clsx from "clsx";
import { type ReactNode } from "react";

import { Callout } from "../components/Callout";
import { Presence } from "../components/presence";
import { codeInputClass, Field, inputClass } from "../components/ui";
import { t } from "../i18n/i18n";

/** Éléments communs aux formulaires des comptes : champs de mot de passe et de code, refus sous le formulaire. Les
 *  secrets ne vivent que dans l'état du formulaire : jamais rangés dans le navigateur, effacés après l'envoi. */

/** Mise en forme d'un code à 16 signes pendant la saisie : majuscules, tirets par groupes de quatre
 *  (`XXXX-XXXX-XXXX-XXXX`), comme le code de transmission. */
export function formatSecretCode(raw: string): string {
  const cleaned = raw.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 16);
  return (cleaned.match(/.{1,4}/g) ?? []).join("-");
}

/** Code complet (16 signes). */
export const codeComplete = (code: string) => code.replace(/-/g, "").length === 16;

/** Champ de mot de passe : `current` pour le mot de passe actuel (gestionnaire de mots de passe d'un poste
 *  personnel), sinon nouveau mot de passe. */
export function PasswordInput({
  value,
  onChange,
  current = false,
  attention,
  autoFocus,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  current?: boolean;
  attention?: string;
  autoFocus?: boolean;
  placeholder?: string;
}) {
  return (
    <input
      type="password"
      data-attention={attention}
      className={inputClass}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete={current ? "current-password" : "new-password"}
      autoFocus={autoFocus}
      maxLength={128}
      spellCheck={false}
      placeholder={placeholder}
    />
  );
}

/** Champ d'un code à 16 signes (activation, réinitialisation, secours, amorçage). */
export function CodeInput({
  value,
  onChange,
  attention,
  autoFocus,
}: {
  value: string;
  onChange: (value: string) => void;
  attention?: string;
  autoFocus?: boolean;
}) {
  return (
    <input
      data-attention={attention}
      className={clsx(codeInputClass, "tracking-wide")}
      value={value}
      onChange={(e) => onChange(formatSecretCode(e.target.value))}
      placeholder="XXXX-XXXX-XXXX-XXXX"
      autoComplete="off"
      autoCapitalize="characters"
      spellCheck={false}
      autoFocus={autoFocus}
    />
  );
}

/** Nouveau mot de passe et sa confirmation, avec l'aide du §8.3. */
export function NewPasswordFields({
  password,
  confirm,
  onPassword,
  onConfirm,
  label,
}: {
  password: string;
  confirm: string;
  onPassword: (value: string) => void;
  onConfirm: (value: string) => void;
  /** Libellé du premier champ (« Mot de passe », « Nouveau mot de passe »). */
  label?: string;
}) {
  return (
    <>
      <Field label={label ?? t("Nouveau mot de passe")} hint={t("12 caractères au moins. Une phrase de passe est plus sûre et plus facile à retenir.")}>
        <PasswordInput value={password} onChange={onPassword} attention="auth-new-password" />
      </Field>
      <Field label={t("Confirmez le mot de passe")}>
        <PasswordInput value={confirm} onChange={onConfirm} attention="auth-confirm-password" />
      </Field>
    </>
  );
}

/** Refus sous le formulaire, montré et retiré en douceur. */
export function FormError({ message, className }: { message: string | null; className?: string }) {
  return (
    <Presence show={Boolean(message)}>
      {message && (
        <Callout tone="danger" className={className} report={false}>
          {message}
        </Callout>
      )}
    </Presence>
  );
}

/** Titre d'un écran des comptes, avec son icône. */
export function AuthTitle({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <h1 className="flex items-center gap-2 text-title font-semibold text-ink">
      <span className="flex text-ink-muted">{icon}</span> {children}
    </h1>
  );
}
